import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@core-ui/Components/ui/button';
import { cn } from '@core-ui-lib/utils';

import { FileText, Loader2, UploadCloud, X } from 'lucide-react';

import useContactsApi from '@features/contacts/hooks/useContactsApi';
import useCsvValidator from '@features/contacts/hooks/useCsvValidator';
import CsvPreviewStats from '@features/contacts/components/csv-preview/CsvPreviewStats';
import CsvPreviewTable from '@features/contacts/components/csv-preview/CsvPreviewTable';
import { useSessionStore } from '@features/auth';
import { logAndToast } from '@lib/logger/logAndToast';
import { logSuccess } from '@lib/logger/logSuccess';

import type { ContactListDto } from '../../types/wizard.types';

interface ContactListUploadPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (list: ContactListDto) => void;
}

export default function ContactListUploadPanel ({
  isOpen,
  onClose,
  onCreated,
}: ContactListUploadPanelProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const { currentUser } = useSessionStore();
  const { createCsvListAsync, uploadCsvToListAsync, downloadContactsTemplateCsv } = useContactsApi();
  const validator = useCsvValidator();

  const [listName, setListName] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const hasPreview = validator.validationResult !== null;

  const handleFileSelect = (file: File | null): void => {
    if (file === null) {
      return;
    }
    const isCsv = file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
    if (!isCsv) {
      return;
    }
    setCsvFile(file);
    validator.validateFile(file);
  };

  const handleClearFile = (): void => {
    setCsvFile(null);
    validator.clearValidation();
  };

  const handleCancel = (): void => {
    setListName('');
    setCsvFile(null);
    validator.clearValidation();
    onClose();
  };

  const handleUpload = async (): Promise<void> => {
    const orgId = currentUser?.organizationId;
    if (orgId == null || csvFile === null || listName.trim() === '') {
      return;
    }

    setIsUploading(true);

    const createResult = await createCsvListAsync({
      organizationId: orgId,
      title: listName.trim(),
      type: 'CSV',
      nonce: 'CSV',
    });

    if (!createResult.success || createResult.data === undefined) {
      const errorMsg = 'error' in createResult && createResult.error?.message != null
        ? createResult.error.message
        : 'Failed to create list';
      await logAndToast({
        message: errorMsg,
        source: 'ContactListUploadPanel',
        toast: true,
        level: 'error',
      });
      setIsUploading(false);
      return;
    }

    const newList = createResult.data;

    const uploadResult = await uploadCsvToListAsync({
      organizationId: orgId,
      contactListId: newList.id,
      file: csvFile,
    });

    if (!uploadResult.success) {
      const uploadErrorMsg = 'error' in uploadResult && uploadResult.error?.message != null
        ? uploadResult.error.message
        : 'Failed to upload CSV';
      await logAndToast({
        message: uploadErrorMsg,
        source: 'ContactListUploadPanel',
        toast: true,
        level: 'error',
      });
    }

    await logSuccess(
      t('contacts.toast.listCreated', 'Contact list created successfully'),
      'ContactListUploadPanel',
      { contactListId: newList.id, title: newList.title },
    );

    setIsUploading(false);
    setListName('');
    setCsvFile(null);
    validator.clearValidation();
    onCreated(newList);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 mb-4 space-y-3 animate-in slide-in-from-top-2 duration-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <FileText className="h-4 w-4" />
          {t('wizard.inlineCreate.contacts.newContactList', 'New Contact List')}
        </div>
        <button
          type="button"
          onClick={handleCancel}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {hasPreview && validator.validationResult !== null ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{listName}</span>
            <span className="text-muted-foreground text-xs">{validator.fileName}</span>
            <button
              type="button"
              onClick={handleClearFile}
              className="ml-auto text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <CsvPreviewStats
            stats={validator.validationResult.stats}
            variant="compact"
          />

          <CsvPreviewTable
            contacts={validator.validationResult.contacts}
            stats={validator.validationResult.stats}
            onUpload={() => {
              void handleUpload();
            }}
            onCancel={handleCancel}
            isUploading={isUploading}
            variant="compact"
            uploadableCount={validator.uploadableCount}
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                {t('wizard.inlineCreate.contacts.listNameLabel', 'List Name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={listName}
                onChange={(e) => {
                  setListName(e.target.value); 
                }}
                placeholder={t('contacts.createListDialog.namePh', 'List name')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                {t('wizard.inlineCreate.contacts.csvFileLabel', 'CSV File')} <span className="text-red-500">*</span>
              </label>
              <label
                className={cn(
                  'flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed px-3 text-sm text-muted-foreground',
                  'hover:bg-muted/40',
                  isDragging && 'bg-muted/50 border-primary',
                )}
                onDragOver={(e) => {
                  e.preventDefault(); setIsDragging(true); 
                }}
                onDragLeave={(e) => {
                  e.preventDefault(); setIsDragging(false); 
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  handleFileSelect(e.dataTransfer.files?.[0] ?? null);
                }}
              >
                <UploadCloud className="h-4 w-4" />
                {t('wizard.inlineCreate.contacts.csvDropzone', 'Drop CSV or')}{' '}
                <span className="text-primary font-medium">{t('wizard.inlineCreate.contacts.browse', 'browse')}</span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    handleFileSelect(e.target.files?.[0] ?? null); 
                  }}
                />
              </label>
            </div>
          </div>

          {csvFile !== null && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              <span>{csvFile.name}</span>
              <span className="text-xs">{(csvFile.size / 1024).toFixed(1)} KB</span>
              <button
                type="button"
                onClick={handleClearFile}
                className="ml-1 hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1 text-xs text-muted-foreground"
              onClick={() => {
                downloadContactsTemplateCsv(); 
              }}
            >
              <FileText className="h-3.5 w-3.5" />
              {t('wizard.inlineCreate.contacts.downloadTemplate', 'Download Template')}
            </Button>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="border border-border text-muted-foreground hover:bg-muted"
              >
                {t('wizard.inlineCreate.contacts.cancel', 'Cancel')}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  void handleUpload(); 
                }}
                disabled={isUploading || listName.trim() === '' || csvFile === null}
                className="gap-1 bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {isUploading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {t('wizard.inlineCreate.contacts.uploadAndSelect', 'Upload & Select')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
